import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "@react-three/drei";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { editorStore } from "../../store/editorStore";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { PolygonSymbolType, zIndexEpsilon } from "../../types/floorplannerTypes";
import {
  createShapePrimitive2DPoints,
  shouldShowShapePrimitiveProjection,
} from "../../features/floorplan3d/symbols/primitives/shapes/shapePrimitiveGeometry";
import DraggableObject from "./DraggableObject";
import PatternFillMesh from "./PatternFillMesh";
import { BoundingBox, SelectableSymbol } from "./SelectableSymbol";
import ShapeProjectionOverlay from "./ShapeProjectionOverlay";
import { createClosedPolygonLinePoints } from "./polygonSymbolPoints";
import { canvasAccentColor } from "../../constants/brandColors";
import { resolveFillOpacity } from "../../utils/fillOpacity";
import { getOpacityRemountEpoch } from "../../utils/opacityRemount";
import { isSymbolAspectRatioLocked } from "../../utils/symbolAspectRatio";

interface PolygonSymbolProps {
  polygon: PolygonSymbolType;
  onDragStart: (polygon: PolygonSymbolType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

const PolygonSymbol: React.FC<PolygonSymbolProps> = observer(({
  polygon,
  onDragStart,
  onDragEnd,
  onDrag,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const floorplannerStore = React.useContext(FloorplannerStoreContext);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const lineWeight = polygon.lineWeight || floorplannerStore.symbolLineWeight;
  const lineColor = polygon.lineColor || floorplannerStore.lineColor;
  const projectionActive = shouldShowShapePrimitiveProjection(polygon);
  const fillOpacity = resolveFillOpacity(polygon);
  const fillIsTransparent = fillOpacity < 1;
  const [polygonWidth, setPolygonWidth] = useState(Number.isFinite(polygon.polygonWidth) ? polygon.polygonWidth : 1);
  const [polygonHeight, setPolygonHeight] = useState(Number.isFinite(polygon.polygonHeight) ? polygon.polygonHeight : 1);
  const [localPosition, setLocalPosition] = useState<[number, number]>([polygon.position.x, polygon.position.y]);
  const points = useMemo(
    () => createShapePrimitive2DPoints(polygon.polygonKind, {
      width: polygonWidth,
      depth: polygonHeight,
    }),
    [polygon.polygonKind, polygonWidth, polygonHeight],
  );
  const linePoints = useMemo(
    () => createClosedPolygonLinePoints(points),
    [points],
  );
  const shape = useMemo(() => {
    const nextShape = new THREE.Shape();
    nextShape.moveTo(points[0][0], points[0][1]);
    points.slice(1).forEach(([x, y]) => nextShape.lineTo(x, y));
    nextShape.closePath();
    return nextShape;
  }, [points]);

  const calculateBoundingBox = useCallback((): BoundingBox => {
    const lineWeightAdjustment = lineWeight / 100;
    const width = polygonWidth + lineWeightAdjustment;
    const height = polygonHeight + lineWeightAdjustment;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    return {
      topLeft: [-halfWidth, halfHeight],
      topRight: [halfWidth, halfHeight],
      bottomLeft: [-halfWidth, -halfHeight],
      bottomRight: [halfWidth, -halfHeight],
      width,
      height,
      depth: 0,
    };
  }, [lineWeight, polygonHeight, polygonWidth]);

  useEffect(() => {
    editorStore.updateGroupRef(polygon.id, groupRef.current);
  }, [polygon.id]);

  useEffect(() => {
    if (isResizing) return;
    setPolygonWidth(Number.isFinite(polygon.polygonWidth) ? polygon.polygonWidth : 1);
    setPolygonHeight(Number.isFinite(polygon.polygonHeight) ? polygon.polygonHeight : 1);
    setLocalPosition([polygon.position.x, polygon.position.y]);
  }, [isResizing, polygon.polygonWidth, polygon.polygonHeight, polygon.position.x, polygon.position.y]);

  return (
    <group position={[0, 0, polygon.zIndex * zIndexEpsilon]}>
      <ShapeProjectionOverlay symbol={polygon} />
      <DraggableObject
        position={localPosition}
        onDragStart={(offset) => {
          setIsDragging(true);
          onDragStart(polygon, offset);
        }}
        onDragEnd={(endPosition) => {
          onDragEnd(endPosition);
          setIsDragging(false);
        }}
        onDrag={onDrag}
        selectable
        attachmentId={polygon.id}
        attachmentType="noAttachments"
        symbol={polygon}
      >
        <SelectableSymbol
          handleSize={floorplannerStore.symbolHandleSize}
          calculateBoundingBox={calculateBoundingBox}
          onResizeStart={() => setIsResizing(true)}
          onResize={(newWidth, newHeight, _boundingBox, _handle, newPosition) => {
            setPolygonWidth(newWidth);
            setPolygonHeight(newHeight);
            if (newPosition) {
              setLocalPosition(newPosition);
            }
          }}
          onResizeEnd={(_event, finalResize) => {
            setIsResizing(false);
            if (!finalResize) return;
            floorplannerStore.updateSymbolProperty(polygon.id, "polygonWidth", finalResize.width);
            floorplannerStore.updateSymbolProperty(polygon.id, "polygonHeight", finalResize.height);
            if (finalResize.position) {
              floorplannerStore.updateSymbolProperty(polygon.id, "position", finalResize.position);
            }
          }}
          center={[0, 0]}
          rotation={polygon.rotation}
          symbol={polygon}
          drawHandles
          pivot={[0, 0]}
          onPointerOver={() => setIsHovered(true)}
          onPointerOut={() => setIsHovered(false)}
          isDragging={isDragging}
          lockAspectRatio={isSymbolAspectRatioLocked(polygon.lockAspectRatio)}
        >
          <group
            key={`polygon-opacity-${Math.round(fillOpacity * 100)}-${getOpacityRemountEpoch(polygon.id)}`}
            ref={groupRef}
            rotation={[0, 0, polygon.rotation || 0]}
            onPointerEnter={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.add("cursor-vector");
            }}
            onPointerLeave={() => {
              if (document.body.style.cursor !== "grabbing" && document.body.style.cursor !== "move") document.body.classList.remove("cursor-vector");
            }}
          >
            <mesh>
              <shapeGeometry args={[shape]} />
              <PatternFillMesh
                fillColor={polygon.fillColor || 0xffffff}
                fillPattern={polygon.fillPattern}
                useWorldSpace={false}
                side={THREE.DoubleSide}
                opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
                transparent={projectionActive || fillIsTransparent}
                depthWrite={!projectionActive && !fillIsTransparent}
              />
            </mesh>
            <Line
              key={`polygon-outline-${Math.round((projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity) * 100)}`}
              points={linePoints}
              color={projectionActive ? 0x94a3b8 : canvasAccentColor(polygon.selected, !!isHovered, lineColor)}
              lineWidth={projectionActive ? 0.8 : lineWeight}
              opacity={projectionActive ? Math.min(fillOpacity, 0.08) : fillOpacity}
              transparent={projectionActive || fillIsTransparent}
              depthWrite={!projectionActive && !fillIsTransparent}
              {...((projectionActive || polygon.lineType === "dashed") && {
                dashed: true,
                dashSize: 0.15,
                gapSize: 0.1,
              })}
              raycast={() => null}
            />
          </group>
        </SelectableSymbol>
      </DraggableObject>
    </group>
  );
});

export default PolygonSymbol;
